/**
 * apps/automations-portal/src/features/demo/DemoPage.tsx
 *
 * Public page — no sign-in required. Lets a visitor pick an automation,
 * choose a canned scenario or paste their own text, and watch a run play
 * out in RunTheatre.
 *
 * The block list comes from GET /api/demo/scenarios — only automations with
 * a provisioned demo appear there, and the list may be empty before
 * anything is provisioned, which is a calm "not available yet" state, not
 * an error.
 */
import { useEffect, useRef, useState } from 'react';
import { Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ApiError, demoApi, isNotAvailable, isNotImplemented, isRateLimited } from '@/lib/api';
import type { DemoBlock, DemoRun } from '@/lib/types';
import { RunTheatre, type RunPhase } from './RunTheatre';

const POLL_INTERVAL_MS = 2000;
const POLL_CEILING_MS = 3 * 60 * 1000;

function PageIntro() {
  return (
    <div className="text-center">
      <h1 className="text-3xl font-semibold">See it work</h1>
      <p className="mt-2 text-muted-foreground">
        Pick an automation, run it against a sample scenario (or your own text), and see what the AI produces. This
        is a preview only — nothing you enter is ever emailed or sent to anyone. Text you
        paste is kept briefly so the page can show you the result, then deleted.
      </p>
    </div>
  );
}

export function DemoPage() {
  const [blocks, setBlocks] = useState<DemoBlock[] | null>(null);
  const [blockId, setBlockId] = useState<string>('');
  const [scenarioId, setScenarioId] = useState<string>('');
  const [customInput, setCustomInput] = useState('');
  const [phase, setPhase] = useState<RunPhase>('idle');
  const [run, setRun] = useState<DemoRun | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [inputError, setInputError] = useState<string | null>(null);

  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollDeadline = useRef<number>(0);

  useEffect(() => {
    demoApi
      .scenarios()
      .then((loaded) => {
        setBlocks(loaded);
        const [first] = loaded;
        if (first) setBlockId(first.blockId);
      })
      .catch(() => setBlocks([]));
  }, []);

  useEffect(() => {
    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
  }, []);

  function stopPolling() {
    if (pollTimer.current) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
  }

  function pollOnce(runId: string) {
    demoApi
      .pollRun(runId)
      .then((next) => {
        setRun(next);
        if (next.status === 'SUCCEEDED' || next.status === 'FAILED' || next.status === 'EXPIRED') {
          setPhase('done');
          stopPolling();
        } else if (Date.now() >= pollDeadline.current) {
          setPhase('timeout');
          stopPolling();
        }
      })
      .catch(() => {
        if (Date.now() >= pollDeadline.current) {
          setPhase('timeout');
          stopPolling();
        }
        // Otherwise: a single failed poll is not fatal — the next tick tries again.
      });
  }

  function handleBlockChange(nextBlockId: string) {
    setBlockId(nextBlockId);
    setScenarioId('');
    setCustomInput('');
    setNotice(null);
    setInputError(null);
  }

  function handleScenarioChange(nextScenarioId: string) {
    setScenarioId(nextScenarioId);
    setCustomInput('');
    setInputError(null);
  }

  async function handleRun() {
    setNotice(null);
    setInputError(null);
    setRun(null);
    setPhase('starting');

    try {
      const started = await demoApi.startRun({
        blockId,
        scenarioId: scenarioId || undefined,
        input: scenarioId ? undefined : customInput.trim() || undefined,
      });
      setRun(started);
      setPhase('polling');
      pollDeadline.current = Date.now() + POLL_CEILING_MS;
      pollTimer.current = setInterval(() => pollOnce(started.id), POLL_INTERVAL_MS);
    } catch (err) {
      setPhase('idle');
      if (isRateLimited(err)) {
        setNotice("You've used today's demo runs. Come back tomorrow, or get in touch for a guided demo.");
      } else if (isNotImplemented(err) || isNotAvailable(err)) {
        setNotice('Demo coming soon — the automation engine is being connected up. Check back shortly.');
      } else if (err instanceof ApiError && (err.code === 'EMPTY_INPUT' || err.code === 'INPUT_TOO_LONG')) {
        setInputError(
          err.code === 'EMPTY_INPUT'
            ? 'Add some text to run the automation against.'
            : "That's a bit long for the demo — try trimming it down.",
        );
      } else {
        setNotice('Could not start that demo run. Please try again.');
      }
    }
  }

  if (blocks === null) {
    return (
      <div className="mx-auto flex max-w-3xl flex-col gap-8 px-6 py-12">
        <PageIntro />
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">Loading the demo…</CardContent>
        </Card>
      </div>
    );
  }

  if (blocks.length === 0) {
    return (
      <div className="mx-auto flex max-w-3xl flex-col gap-8 px-6 py-12">
        <PageIntro />
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            The demo isn&apos;t available just yet — check back shortly, or get in touch for a guided walkthrough.
          </CardContent>
        </Card>
      </div>
    );
  }

  const firstBlock = blocks[0];
  if (!firstBlock) {
    // Unreachable — the `blocks.length === 0` branch above already returned
    // — but noUncheckedIndexedAccess can't see that, so this satisfies it.
    return null;
  }
  const selectedBlock = blocks.find((block) => block.blockId === blockId) ?? firstBlock;
  const hasContent = scenarioId !== '' || customInput.trim().length > 0;
  const canRun = phase !== 'starting' && phase !== 'polling' && hasContent;
  const selectedScenario = selectedBlock.scenarios.find((scenario) => scenario.id === scenarioId) ?? null;

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-8 px-6 py-12">
      <PageIntro />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Set up a run</CardTitle>
          <CardDescription>Choose an automation and what to run it against.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">Automation</span>
            <Select value={selectedBlock.blockId} onValueChange={handleBlockChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {blocks.map((block) => (
                  <SelectItem key={block.blockId} value={block.blockId}>
                    {block.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedBlock.description && (
              <p className="text-xs text-muted-foreground">{selectedBlock.description}</p>
            )}
          </div>

          {selectedBlock.scenarios.length > 0 && (
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium">Scenario</span>
              <Select value={scenarioId} onValueChange={handleScenarioChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a sample scenario, or paste your own below" />
                </SelectTrigger>
                <SelectContent>
                  {selectedBlock.scenarios.map((scenario) => (
                    <SelectItem key={scenario.id} value={scenario.id}>
                      {scenario.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedScenario && (
                <div className="whitespace-pre-wrap rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
                  {selectedScenario.inputText}
                </div>
              )}
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">Or paste your own</span>
            <Textarea
              value={customInput}
              onChange={(e) => {
                setCustomInput(e.target.value);
                if (e.target.value) setScenarioId('');
                setInputError(null);
              }}
              placeholder="Paste an email, a call transcript, or a review — whatever you'd like to see automated."
              rows={4}
            />
            {inputError && <p className="text-sm text-destructive">{inputError}</p>}
          </div>

          {notice && <p className="text-sm text-muted-foreground">{notice}</p>}

          <Button type="button" onClick={handleRun} disabled={!canRun} className="w-fit">
            <Play className="h-4 w-4" />
            {phase === 'starting' || phase === 'polling' ? 'Running…' : 'Run the automation'}
          </Button>
          <p className="text-xs text-muted-foreground">
            A preview only — nothing here is emailed or sent to anyone.
          </p>
        </CardContent>
      </Card>

      <RunTheatre run={run} phase={phase} />
    </div>
  );
}
